import React, { useCallback } from 'react';
import { Button, useNoticeBannersStackContext } from '@sweetgreen/garnish';

import { ApplePay } from '@order/components';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';
import { convertPriceToFloat } from '@order/utils';

import { type useCheckoutContext } from '../../CheckoutProvider';

// ────────────────────────────────────────────────────────────────────────────

export const CheckoutPlaceOrderButton = (props: CheckoutOrderBlockProps) => {
  const {
    payload,
    orderId,
    total,
    isSubmittingOrder,
    cannotSubmitOrderReason,
    shouldBlockCheckoutOnPress,
    handleInvalidWantedTime,
    validateCheckoutFields,
    submitOrder,
  } = props;

  // ─── Context ──────────────────────────────────────────────────────────────

  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { track } = useTelemetry();
  const isApplePayEnabled = useFeatureFlag('permanent-apple-pay-enabled');

  // ─── Validation ───────────────────────────────────────────────────────────

  const validateCanPlaceOrder = useCallback(
    (paymentMethodId?: string) => {
      validateCheckoutFields({
        ...payload,
        paymentMethodId: paymentMethodId ?? payload.paymentMethodId,
      });

      const hasExternalPaymentMethod = Boolean(paymentMethodId);
      const isMissingPaymentMethod =
        cannotSubmitOrderReason === t('checkout.cannot-submit.payment-method');
      const shouldSkipPaymentMethodCheck =
        isMissingPaymentMethod && hasExternalPaymentMethod;

      const shouldShowError =
        !orderId ||
        (shouldBlockCheckoutOnPress && !shouldSkipPaymentMethodCheck);

      if (shouldShowError) {
        addNoticeBanner({ text: cannotSubmitOrderReason, palette: 'caution' });

        return false;
      }

      return true;
    },
    [
      payload,
      cannotSubmitOrderReason,
      orderId,
      shouldBlockCheckoutOnPress,
      t,
      validateCheckoutFields,
      addNoticeBanner,
    ],
  );

  // ─── Submit Order ─────────────────────────────────────────────────────────

  const submitOrderCallback = useCallback(async () => {
    if (orderId && validateCanPlaceOrder()) {
      await submitOrder({
        input: {
          ...payload,
          orderId,
        },
        handleInvalidWantedTime,
      });
    }
  }, [
    orderId,
    payload,
    handleInvalidWantedTime,
    submitOrder,
    validateCanPlaceOrder,
  ]);

  const submitOrderWithApplePay = useCallback(
    async (paymentMethodId: string) => {
      if (orderId && validateCanPlaceOrder(paymentMethodId)) {
        track('checkout.apple-pay', {
          ...payload,
          orderValue: convertPriceToFloat(total),
        });

        await submitOrder({
          input: {
            ...payload,
            paymentMethodId,
            paymentMethodType: 'PaymentMethodCard',
            orderId,
          },
          handleInvalidWantedTime,
        });
      }
    },
    [
      orderId,
      total,
      payload,
      track,
      validateCanPlaceOrder,
      handleInvalidWantedTime,
      submitOrder,
    ],
  );

  return (
    <>
      <Button
        size="large"
        palette="primary"
        isLoading={isSubmittingOrder}
        accessibilityLabel={t('checkout.place-order')}
        onPress={submitOrderCallback}
      >
        {t('checkout.place-order')}
      </Button>

      {isApplePayEnabled ? (
        <ApplePay.Checkout
          amountToCapture={total}
          isSubmitting={isSubmittingOrder}
          validateCanPlaceOrder={validateCanPlaceOrder}
          onCheckout={submitOrderWithApplePay}
        />
      ) : null}
    </>
  );
};

// ─── Types ──────────────────────────────────────────────────────────────────

type CheckoutOrderBlockProps = Pick<
  ReturnType<typeof useCheckoutContext>,
  | 'payload'
  | 'orderId'
  | 'checkoutCredit'
  | 'paymentMethods'
  | 'isSubmittingOrder'
  | 'cannotSubmitOrderReason'
  | 'shouldBlockCheckoutOnPress'
  | 'handleInvalidWantedTime'
  | 'validateCheckoutFields'
  | 'submitOrder'
  | 'total'
>;
